import { FormHelperText, FormLabel, Grid, MenuItem, FormControl, TextField } from "@mui/material";
import { FC, useEffect, useState } from "react";
import { AddressLookup } from "./AddressLookup";
import { FormInput } from "../CoreLib/library";
import { countries } from "../../util";
import { StateAutocomplete } from "./StateAutocomplete";
import { Address } from "../../dtos/Address";
import { ClearableSelect } from "./ClearableSelect";

export interface IAddressFormProps {
    handleAddressChange?: (address: Address) => void;
    enableLookup?: boolean;
    disabled?: boolean;
    addressLine1: string;
    addressLine2?: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
    directions?: string;
    fieldErrors?: any;
    formDirection?: 'row' | 'column';
    allOptional?: boolean;
    enableAddressLine2?: boolean;
};

export const AddressForm: FC<IAddressFormProps> = props => {
    const {
        handleAddressChange,
        enableLookup = false,
        disabled,
        addressLine1,
        addressLine2,
        country,
        directions,
        city,
        state,
        zipCode,
        fieldErrors,
        formDirection = 'row',
        allOptional = false,
        enableAddressLine2,
    } = props;

    const [formDirections, setFormDirections] = useState(directions ?? '');

    useEffect(() => {
        setFormDirections(directions ?? '');
    }, [addressLine1, addressLine2, city, state, zipCode, country, directions]);

    const handleAddressPartialChange = (partialAddress: Partial<Address>) => {
        if (handleAddressChange) {
            const fullAddress: Address = {
                addressLine1: addressLine1,
                addressLine2: addressLine2 ?? '',
                city: city,
                state: state,
                zipCode: zipCode,
                country: country,
                directions: directions,
                ...partialAddress
            };
            handleAddressChange(fullAddress);
        }
    };

    return (
        <Grid item container direction='column' spacing={3}>
            {enableLookup && <Grid item container direction='row' spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                    <AddressLookup
                        handleAddressChange={handleAddressChange}
                    />
                </Grid>
            </Grid>}
            <Grid item container direction={formDirection} spacing={formDirection === 'row' ? 2 : 1} wrap="nowrap">
                <Grid item xs={12} sm={6} md={2}>
                    <FormInput
                        value={addressLine1}
                        onChange={(event) => {
                            handleAddressPartialChange({ addressLine1: event.target.value });
                        }}
                        label='Address Line 1'
                        name='addressLine1'
                        errorText={fieldErrors?.get('addressLine1')}
                        fullWidth
                        required={!allOptional}
                        disabled={disabled}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                    <FormInput
                        value={addressLine2 ?? ''}
                        onChange={(event) => {
                            handleAddressPartialChange({ addressLine2: event.target.value });
                        }}
                        label='Address Line 2'
                        name='addressLine2'
                        fullWidth
                        disabled={disabled && !enableAddressLine2}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                    <FormInput
                        value={city}
                        onChange={(event) => {
                            handleAddressPartialChange({ city: event.target.value });
                        }}
                        label='City'
                        name='city'
                        errorText={fieldErrors?.get('city')}
                        fullWidth
                        required={!allOptional}
                        disabled={disabled}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                    <StateAutocomplete
                        error={!!fieldErrors?.get('state')}
                        fullWidth
                        disabled={disabled}
                        required={!allOptional}
                        value={state}
                        onChange={(event, value) => {
                            handleAddressPartialChange({ state: value ?? "" });
                        }}
                        fieldError={fieldErrors?.get('state')}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                    <FormInput
                        value={zipCode}
                        onChange={(event) => {
                            handleAddressPartialChange({ zipCode: event.target.value });
                        }}
                        label='Zip Code'
                        name='zipCode'
                        errorText={fieldErrors?.get('zipCode')}
                        fullWidth
                        required={!allOptional}
                        disabled={disabled}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                    <FormControl error={!!fieldErrors?.get('country')} fullWidth required={!allOptional}
                        disabled={disabled}
                    >
                        <FormLabel>Country</FormLabel>
                        <ClearableSelect
                            value={country ?? ''}
                            disabled={disabled}
                            onChange={(event) => {
                                handleAddressPartialChange({ country: event.target.value });
                            }}
                            onClear={() => handleAddressPartialChange({ country: '' })}
                        >
                            {countries.map((country) => (
                                <MenuItem key={country} value={country}>
                                    {country}
                                </MenuItem>
                            ))}
                        </ClearableSelect>
                        <FormHelperText>{fieldErrors?.get('country')}</FormHelperText>
                    </FormControl>
                </Grid>
            </Grid>
            <Grid item container direction={formDirection} spacing={formDirection === 'row' ? 2 : 1} wrap="nowrap">
                <Grid item xs={12} sm={12} md={12} sx={{ marginBottom: 2 }}>
                    <TextField
                        label='Directions'
                        value={formDirections}
                        onChange={(event) => {
                            setFormDirections(event.target.value);
                        }}
                        onBlur={(event) => {
                            handleAddressPartialChange({ directions: event.target.value });
                        }}
                        fullWidth
                        multiline
                        minRows={3}
                        disabled={disabled}
                    />
                </Grid>
            </Grid>
        </Grid>
    );
};